<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';
import type { Matter } from '../../stores/matterStore';
import CreateMatterModal from './CreateMatterModal.vue';
import DeleteMatterModal from './DeleteMatterModal.vue';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';

const router = useRouter();
const { t } = useI18n();
const matterStore = useMatterStore();

const searchQuery = ref('');
const showCreateMatter = ref(false);
type ViewMode = 'list' | 'grid';
const VIEW_MODE_STORAGE_KEY = 'matters.viewMode';
const storedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
const viewMode = ref<ViewMode>(storedViewMode === 'grid' ? 'grid' : 'list');
watch(viewMode, (mode) => localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode));
type SortColumn = 'Name' | 'ClientName';
const sortColumn = ref<SortColumn>('Name');
const sortDirection = ref<'asc' | 'desc'>('asc');

// Client filter. Sentinel value buckets matters with a blank client name.
const NO_CLIENT = '__no_client__';
const showClientFilter = ref(false);
const clientFilterQuery = ref('');
const selectedClients = ref<string[]>([]);

const clientKey = (m: Matter) => (m.ClientName?.trim() ? m.ClientName : NO_CLIENT);

// Distinct clients derived from the loaded matters, with a count each. Real
// names sort alphabetically; the "No client" bucket (if any) sorts last.
const clientOptions = computed(() => {
    const counts = new Map<string, number>();
    for (const m of matterStore.matters) {
        const key = clientKey(m);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const named = [...counts.entries()]
        .filter(([value]) => value !== NO_CLIENT)
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    if (counts.has(NO_CLIENT)) {
        named.push({ value: NO_CLIENT, label: t('matters.noClient'), count: counts.get(NO_CLIENT)! });
    }
    return named;
});

const filteredClientOptions = computed(() => {
    const q = clientFilterQuery.value.trim().toLowerCase();
    if (!q) return clientOptions.value;
    return clientOptions.value.filter((opt) => opt.label.toLowerCase().includes(q));
});

const activeClientCount = computed(() => selectedClients.value.length);

const isClientSelected = (value: string) => selectedClients.value.includes(value);

const toggleClient = (value: string) => {
    selectedClients.value = isClientSelected(value)
        ? selectedClients.value.filter((v) => v !== value)
        : [...selectedClients.value, value];
};

const clearClientFilter = () => {
    selectedClients.value = [];
};

const toggleClientFilter = () => {
    showClientFilter.value = !showClientFilter.value;
};

const onWindowClick = (event: MouseEvent) => {
    if (!showClientFilter.value) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.matters-client-filter')) return;
    showClientFilter.value = false;
};

const onWindowKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && showClientFilter.value) showClientFilter.value = false;
};

const sortBy = (column: SortColumn) => {
    if (sortColumn.value === column) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortColumn.value = column;
        sortDirection.value = 'asc';
    }
};

const sortIcon = (column: SortColumn) => {
    if (sortColumn.value !== column) return 'unfold_more';
    return sortDirection.value === 'asc' ? 'arrow_upward' : 'arrow_downward';
};

const filteredMatters = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    let matched = !q
        ? matterStore.matters
        : matterStore.matters.filter((m) => {
              return [m.Name, m.MatterKey, m.ClientName, m.Description]
                  .filter(Boolean)
                  .some((field) => String(field).toLowerCase().includes(q));
          });

    if (selectedClients.value.length) {
        const selected = new Set(selectedClients.value);
        matched = matched.filter((m) => selected.has(clientKey(m)));
    }

    const dir = sortDirection.value === 'asc' ? 1 : -1;
    const key = sortColumn.value;
    return [...matched].sort(
        (a, b) =>
            dir * (a[key] || '').localeCompare(b[key] || '', undefined, { sensitivity: 'base' }),
    );
});

const selectMatter = (matter: Matter) => {
    matterStore.selectMatter(matter);
    // Navigate straight to the new-chat route so the matter opens without
    // waiting on the session list. MatterPage redirects to the most recent
    // dialogue once sessions finish loading (if any exist).
    router.push({ name: 'matter', params: { matterId: matter.RecordId } });
};

// Right-click a matter row/card for the (deliberately low-key) delete entry
// point. Hold ctrl/cmd to fall through to the native browser menu, matching
// the app-header context-menu convention.
const rowContextMenu = ref<{ x: number; y: number } | null>(null);
const contextMenuMatter = ref<Matter | null>(null);
const matterToDelete = ref<Matter | null>(null);

const onMatterContextMenu = (event: MouseEvent, matter: Matter) => {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    contextMenuMatter.value = matter;
    rowContextMenu.value = { x: event.clientX, y: event.clientY };
};

const closeRowContextMenu = () => {
    rowContextMenu.value = null;
    contextMenuMatter.value = null;
};

const rowMenuItems = computed<ContextMenuItem[]>(() => {
    const matter = contextMenuMatter.value;
    if (!matter) return [];
    return [
        {
            label: t('matters.deleteMatter'),
            icon: 'delete',
            onSelect: () => {
                matterToDelete.value = matter;
            },
        },
    ];
});

onMounted(async () => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onWindowKeydown);
    if (!matterStore.hasMatters) {
        try {
            await matterStore.loadMatters();
        } catch {
            // Error is handled in the store
        }
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('click', onWindowClick);
    window.removeEventListener('keydown', onWindowKeydown);
});
</script>

<template>
    <div class="matters-page">
        <div class="matters-header">
            <h1 class="matters-title">{{ t('matters.title') }}</h1>
            <button class="matters-new-button" @click="showCreateMatter = true">
                <span class="material-icons">add</span>
                <span>{{ t('matters.newMatter') }}</span>
            </button>
        </div>

        <div class="matters-toolbar">
            <div class="matters-filter-chips">
                <div class="matters-client-filter">
                    <button
                        class="matters-chip"
                        :class="{ 'matters-chip--active': activeClientCount > 0 }"
                        :aria-expanded="showClientFilter"
                        @click="toggleClientFilter"
                    >
                        <span class="material-icons matters-chip-icon">business</span>
                        <span>{{
                            activeClientCount
                                ? t('matters.clientFilterActive', { count: activeClientCount })
                                : t('matters.clientFilter')
                        }}</span>
                        <span class="material-icons matters-chip-caret">expand_more</span>
                    </button>

                    <div v-if="showClientFilter" class="matters-client-dropdown">
                        <div class="matters-client-dropdown-search">
                            <input
                                v-model="clientFilterQuery"
                                type="text"
                                class="matters-client-search-input"
                                :placeholder="t('matters.searchClients')"
                            />
                            <span class="material-icons matters-client-search-icon">search</span>
                        </div>

                        <div class="matters-client-dropdown-list">
                            <label
                                v-for="opt in filteredClientOptions"
                                :key="opt.value"
                                class="matters-client-option"
                            >
                                <input
                                    type="checkbox"
                                    :checked="isClientSelected(opt.value)"
                                    @change="toggleClient(opt.value)"
                                />
                                <span class="matters-client-option-label">{{ opt.label }}</span>
                                <span class="matters-client-option-count">{{ opt.count }}</span>
                            </label>
                            <div
                                v-if="filteredClientOptions.length === 0"
                                class="matters-client-empty"
                            >
                                {{ t('matters.noClientsMatch') }}
                            </div>
                        </div>

                        <div class="matters-client-dropdown-footer">
                            <button
                                class="matters-client-clear"
                                :disabled="activeClientCount === 0"
                                @click="clearClientFilter"
                            >
                                {{ t('matters.clearFilter') }}
                            </button>
                        </div>
                    </div>
                </div>
                <!-- <button
                    class="matters-chip matters-chip--accent matters-chip--disabled"
                    disabled
                    title="Filter by assignment — coming soon"
                >
                    <span class="material-icons matters-chip-icon">account_circle</span>
                    <span>Assigned to me</span>
                    <span class="material-icons matters-chip-caret">expand_more</span>
                </button> -->
            </div>

            <div class="matters-toolbar-right">
                <div class="matters-view-toggle" role="group" :aria-label="t('matters.viewMode')">
                    <button
                        class="matters-view-toggle-btn"
                        :class="{ 'matters-view-toggle-btn--active': viewMode === 'list' }"
                        :aria-pressed="viewMode === 'list'"
                        :aria-label="t('matters.listView')"
                        :title="t('matters.listView')"
                        @click="viewMode = 'list'"
                    >
                        <span class="material-icons">view_list</span>
                    </button>
                    <button
                        class="matters-view-toggle-btn"
                        :class="{ 'matters-view-toggle-btn--active': viewMode === 'grid' }"
                        :aria-pressed="viewMode === 'grid'"
                        :aria-label="t('matters.gridView')"
                        :title="t('matters.gridView')"
                        @click="viewMode = 'grid'"
                    >
                        <span class="material-icons">grid_view</span>
                    </button>
                </div>
                <div class="matters-search">
                    <input
                        v-model="searchQuery"
                        type="text"
                        class="matters-search-input"
                        :placeholder="t('matters.search')"
                    />
                    <span class="material-icons matters-search-icon">search</span>
                </div>
            </div>
        </div>

        <div v-if="matterStore.loading" class="loading">{{ t('matters.loading') }}</div>
        <div v-else-if="matterStore.error" class="error">{{ matterStore.error }}</div>

        <div v-else-if="filteredMatters.length === 0" class="matters-empty">
            {{ t('matters.noMattersMatch') }}
        </div>

        <div v-else-if="viewMode === 'list'" class="matters-table">
            <div class="matters-table-header">
                <div
                    class="matters-col-matter matters-col-sortable"
                    role="button"
                    tabindex="0"
                    :aria-sort="
                        sortColumn === 'Name'
                            ? sortDirection === 'asc'
                                ? 'ascending'
                                : 'descending'
                            : 'none'
                    "
                    @click="sortBy('Name')"
                    @keydown.enter.prevent="sortBy('Name')"
                    @keydown.space.prevent="sortBy('Name')"
                >
                    <span>{{ t('matters.columnMatter') }}</span>
                    <span
                        class="material-icons matters-sort-icon"
                        :class="{ 'matters-sort-icon--active': sortColumn === 'Name' }"
                    >
                        {{ sortIcon('Name') }}
                    </span>
                </div>
                <div
                    class="matters-col-client matters-col-sortable"
                    role="button"
                    tabindex="0"
                    :aria-sort="
                        sortColumn === 'ClientName'
                            ? sortDirection === 'asc'
                                ? 'ascending'
                                : 'descending'
                            : 'none'
                    "
                    @click="sortBy('ClientName')"
                    @keydown.enter.prevent="sortBy('ClientName')"
                    @keydown.space.prevent="sortBy('ClientName')"
                >
                    <span>{{ t('matters.columnClientName') }}</span>
                    <span
                        class="material-icons matters-sort-icon"
                        :class="{ 'matters-sort-icon--active': sortColumn === 'ClientName' }"
                    >
                        {{ sortIcon('ClientName') }}
                    </span>
                </div>
                <div class="matters-col-desc">{{ t('matters.columnDescription') }}</div>
                <div class="matters-col-updates">{{ t('matters.columnRecentUpdates') }}</div>
            </div>

            <div
                v-for="matter in filteredMatters"
                :key="matter.RecordId"
                class="matters-row"
                @click="selectMatter(matter)"
                @contextmenu="onMatterContextMenu($event, matter)"
            >
                <div class="matters-col-matter">
                    <span
                        class="matters-color-dot"
                        :style="{ backgroundColor: matter.ColorHint || '#bbb' }"
                    ></span>
                    <div class="matters-name-block">
                        <span class="matters-name">{{ matter.Name }}</span>
                        <span v-if="matter.MatterKey" class="matters-key">{{
                            matter.MatterKey
                        }}</span>
                    </div>
                </div>
                <div class="matters-col-client">{{ matter.ClientName || '—' }}</div>
                <div class="matters-col-desc">{{ matter.Description || '—' }}</div>
                <div class="matters-col-updates">
                    <span class="matters-update-count">
                        <span class="material-icons">description</span>
                        <span>{{ matter.RecentUpdates?.documents ?? '—' }}</span>
                    </span>
                    <span class="matters-update-count">
                        <span class="material-icons">chat_bubble_outline</span>
                        <span>{{ matter.RecentUpdates?.comments ?? '—' }}</span>
                    </span>
                </div>
            </div>
        </div>

        <div v-else class="matters-grid">
            <div
                v-for="matter in filteredMatters"
                :key="matter.RecordId"
                class="matters-card"
                @click="selectMatter(matter)"
                @contextmenu="onMatterContextMenu($event, matter)"
            >
                <span class="matters-card-client">{{ matter.ClientName || t('matters.noClient') }}</span>
                <div class="matters-card-name-block">
                    <span
                        class="matters-color-dot"
                        :style="{ backgroundColor: matter.ColorHint || '#bbb' }"
                    ></span>
                    <span class="matters-card-name">{{ matter.Name }}</span>
                </div>
                <p class="matters-card-desc">{{ matter.Description || '—' }}</p>
                <div class="matters-card-updates">
                    <span class="matters-update-count">
                        <span class="material-icons">description</span>
                        <span>{{ matter.RecentUpdates?.documents ?? '—' }}</span>
                    </span>
                    <span class="matters-update-count">
                        <span class="material-icons">chat_bubble_outline</span>
                        <span>{{ matter.RecentUpdates?.comments ?? '—' }}</span>
                    </span>
                </div>
            </div>
        </div>

        <CreateMatterModal v-if="showCreateMatter" @close="showCreateMatter = false" />

        <ContextMenu
            :position="rowContextMenu"
            :items="rowMenuItems"
            @close="closeRowContextMenu"
        />

        <DeleteMatterModal
            v-if="matterToDelete"
            :matter="matterToDelete"
            @close="matterToDelete = null"
            @deleted="matterToDelete = null"
        />
    </div>
</template>
